










import { Component, Prop, Vue } from "@/shared/vuepack";
import _ from "lodash";
import moment from "moment";
import { ChartPointDataType, ChartPoint, DrivewealthAccountIdentifierArgs } from "@/shared/types";
import { formatCurrency } from "@/shared/currency";
import { GChart } from "vue-google-charts";
import { useThemeStore } from "@/shared/store/theme";
import { useDrivewealthAccountPerformanceStore } from "@/shared/store/drivewealthAccountPerformance";

@Component({
  components: {
    GChart
  },
  computed: {
    ChartPointDataType () {
      return ChartPointDataType;
    }
  }
})
export default class InvestmentGenericLineGraph extends Vue {
  @Prop({ default: 220 })
  height: number;

  @Prop({ required: false })
  username: string;

  @Prop({ required: false })
  partnerId: number;

  @Prop({ required: true })
  accountId: number;

  @Prop({ required: true })
  dataValueDisplayName: string;

  @Prop({ required: true })
  dataValuePropertyName: string;

  @Prop({ required: false })
  chartPointDataType: ChartPointDataType;

  themeStore = useThemeStore();
  drivewealthAccountPerformanceStore = useDrivewealthAccountPerformanceStore();

  manualXAxisLabelCutoff = 7;

  get getChartOptions (): any {
    return {
      fontName: this.themeStore.fontFace,
      annotations: {
        color: this.themeStore.tertiaryColor
      },
      titlePosition: "none",
      colors: [ "#B2BCBB" ],
      tooltip: {
        isHtml: true,
      },
      chartArea: {
        height: "78%",
      },
      hAxis: {
        format: "M/dd/yy",
        gridlines: {
          color: "transparent"
        },
        ticks: this.xAxisLabels
      },
      vAxis: {
        baselineColor: "black",
        format: (this.chartPointDataType === ChartPointDataType.Currency ? "currency" : null),
        gridlines: {
          color: "transparent",
        },
        minorGridlines: {
          color: "transparent",
        }
      },
      pointSize: 8,
      height: this.height,
      legend: { position: "none" },
      enableInteractivity: true,
    };
  }

  mounted (): void {
    this.getAccountPerformance();
  }

  getAccountPerformance (): void {
    this.drivewealthAccountPerformanceStore.getAccountPerformance(this.accountIdentifiers);
  }

  get isLoaded (): boolean {
    return this.drivewealthAccountPerformanceStore.accountPerformanceLoaded(this.accountIdentifiers);
  }

  get accountIdentifiers (): DrivewealthAccountIdentifierArgs {
    return {
      username: this.username,
      partnerId: this.partnerId,
      accountId: this.accountId
    };
  }

  get chartData (): ChartPoint[] {
    const accountPerformance = this.drivewealthAccountPerformanceStore.accountPerformanceResult(this.accountIdentifiers);
    let accountPerformanceChartPoints: ChartPoint[] = [];
    if (accountPerformance) {
      accountPerformanceChartPoints = _.map(accountPerformance.performanceReturns, x => {

        return {
          value: x[this.dataValuePropertyName],
          date: moment(x.date).format("DD-MMM-YY"),
        };
      });
    }

    return accountPerformanceChartPoints;
  }

  tooltipFormatter (chartPoint: ChartPoint): string {
    const valueMarkup = this.chartPointDataType === ChartPointDataType.Currency ?
        formatCurrency(chartPoint.value) : chartPoint.value;

    const mainMarkup = `<div style="font-weight: bold; margin-bottom: 3px;">
                          ${moment(chartPoint.date, "DD-MMM-YY").format("MMM DD")}
                        </div>
                        <div>
                          ${this.dataValueDisplayName}:
                        </div>
                        <div style="color: ${this.themeStore.tertiaryColor}; font-weight: bold;">
                          ${valueMarkup}
                        </div>`;

    const tooltipHtml = `<div style="padding: 2px 10px; min-width: 120px;">
                          ${mainMarkup}
                        </div>`;

    return tooltipHtml;
  }

  formatChartPoint (chartPoint: ChartPoint): any {
    return [
      moment(chartPoint.date, "DD-MMM-YY").toDate(),
      chartPoint.value,
      `stroke-color: ${this.themeStore.tertiaryColor}; fill-color: transparent; fill-opacity: 0`,
      this.tooltipFormatter(chartPoint),
    ];
  }

  // If we have too few dates, google charts will try to interpolate the date value,
  // resulting in duplcated dates on the X axis. If there are too few, we need to set
  // the x-axis labels manually. Otherwise, let google do its thing.
  get xAxisLabels (): Date[] {
    const distinctDates = _.uniq(this.chartData.map(x => x.date));

    return distinctDates.length >= this.manualXAxisLabelCutoff ?
        null : distinctDates.map(x => moment(x, "DD-MMM-YY").toDate());
  }

  get formattedChartData (): any {
    const formattedChartPoints = this.chartData.map(this.formatChartPoint);

    return [
      [
        "Date",
        this.dataValueDisplayName,
        { type: "string", role: "style" },
        {
          type: "string", role: "tooltip", p: {
            html: true
          }
        },
      ],
      ...formattedChartPoints
    ];
  }
}
